<template>
  <Dialog
    v-model="visible"
    :closable="true"
  >
    <div class="act-time-dialog">
      <div class="main-text">
        {{ props.type === 'ended' ? $t('monopoly.ended') : $t('monopoly.notStart') }}
      </div>
      <div
        v-if="props.type === 'not-start'"
        class="countdown-block"
      >
        <div class="countdown-label">[{{ $t('monopoly.startCountdown') }}]</div>
        <div class="countdown-value">
          {{ startCountdown.day }} {{ $t('monopoly.day') }}
          {{ startCountdown.hour }}:{{ startCountdown.minute }}:{{ startCountdown.second }}
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'
import Dialog from '../Dialog/index.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'not-start', // not-start | ended
  },
  // 父组件已解析好的毫秒时间戳
  startMs: {
    type: Number,
    default: 0,
  },
  serverMs: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:modelValue', 'countdown-end'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const initialDiffSec = ref(0)
const elapsedSec = ref(0)
let timer = null

const clearTimer = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

const resetCountdown = () => {
  const startMs = Number(props.startMs) || 0
  const serverMs = Number(props.serverMs) || 0
  if (startMs > 0 && serverMs > 0 && startMs > serverMs) {
    initialDiffSec.value = Math.floor((startMs - serverMs) / 1000)
  } else {
    initialDiffSec.value = 0
  }
  elapsedSec.value = 0
}

const startTimer = () => {
  clearTimer()
  resetCountdown()
  timer = setInterval(() => {
    elapsedSec.value += 1
  }, 1000)
}

const countdownDiff = computed(() => {
  if (props.type !== 'not-start' || !visible.value) return -1
  if (!props.startMs || !props.serverMs) return -1
  return Math.max(0, initialDiffSec.value - elapsedSec.value)
})

const pad2 = (num) => (num >= 10 ? String(num) : `0${num}`)

const startCountdown = computed(() => {
  const res = {
    day: '0',
    hour: '00',
    minute: '00',
    second: '00',
  }
  const diff = countdownDiff.value
  if (diff < 0) return res
  const day = Math.floor(diff / 86400)
  const hour = Math.floor((diff % 86400) / 3600)
  const minute = Math.floor((diff % 3600) / 60)
  const second = diff % 60
  res.day = String(day)
  res.hour = pad2(hour)
  res.minute = pad2(minute)
  res.second = pad2(second)
  return res
})

watch(
  () => [props.modelValue, props.type, props.startMs, props.serverMs],
  ([val]) => {
    if (val && props.type === 'not-start') {
      startTimer()
    } else {
      clearTimer()
    }
  },
  { immediate: true }
)

watch(
  () => countdownDiff.value,
  (diff, prev) => {
    if (
      props.type === 'not-start'
      && props.modelValue
      && diff === 0
      && typeof prev === 'number'
      && prev > 0
    ) {
      clearTimer()
      visible.value = false
      emit('countdown-end')
    }
  }
)

onBeforeUnmount(() => {
  clearTimer()
})
</script>

<style lang="less" scoped>
.act-time-dialog {
  width: 100%;
  text-align: center;
  color: #333333;
  padding: 24px 12px;
}

.main-text {
  font-size: 32px;
  font-weight: bold;
  line-height: 1.5;
}

.countdown-block {
  margin-top: 28px;
  font-size: 28px;
  line-height: 1.6;
}

.countdown-label {
  margin-bottom: 8px;
}

.countdown-value {
  font-weight: bold;
  color: #e86a00;
}
</style>
